import React, { useState, useEffect, useRef } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import PayPalButton from './PayPalButton';

const ESP32Generator = ({ userId }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [diagramInstruction, setDiagramInstruction] = useState('');
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(null);
  const [diagramUrl, setDiagramUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [lastCode, setLastCode] = useState('');
  const chatRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const res = await fetch('https://holmio.net/app/api_get_credits.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId })
        });
        const data = await res.json();
        if (data.credits !== undefined) {
          setCredits(data.credits);
        }
      } catch (error) {
        console.error('Error al cargar créditos:', error);
      }
    };
    if (userId) fetchCredits();
  }, [userId]);

  const handleSend = async () => {
    if (!input.trim() || credits <= 0) return;

    setMessages(prev => [...prev, { role: 'user', text: input }]);
    setInput('');
    setLoading(true);

    const lowerInput = input.toLowerCase();
    const isDiagramRequest =
      lowerInput.includes('diagrama') ||
      lowerInput.includes('esquema') ||
      lowerInput.includes('dibuja') ||
      lowerInput.includes('circuito');

    if (isDiagramRequest) {
      if (!lastCode) {
        setMessages(prev => [
          ...prev,
          { role: 'assistant', text: 'Primero debes generar un código antes de crear el diagrama.' }
        ]);
        setLoading(false);
        return;
      }

      try {
        const res = await fetch('https://holmio.net/app/api_esp32.php?diagram=1', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId, codigo: lastCode })
        });
        const data = await res.json();
        if (data.error) {
          setMessages(prev => [...prev, { role: 'assistant', text: data.error }]);
        } else if (data.diagramUrl) {
          setDiagramUrl(data.diagramUrl);
          setCredits(data.tokensRestantes);
          setMessages(prev => [...prev, { role: 'assistant', text: 'Aquí tienes el diagrama basado en tu código.' }]);
        }
      } catch (error) {
        console.error('Error al generar diagrama:', error);
      }

      setLoading(false);
      return;
    }

    setMessages(prev => [...prev, { role: 'assistant', text: 'Generando...', code: '' }]);

    const finalPrompt = lastCode
      ? `Este es el código actual:\n${lastCode}\nPor favor, modifica según esta instrucción: ${input}`
      : input;

    const response = await fetch('https://holmio.net/app/api_esp32_stream.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question: finalPrompt, userId })
    });

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let partialText = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });

      const events = chunk.split("\n\n");
      events.forEach(event => {
        if (event.startsWith("event: message")) {
          const dataLine = event.split("\ndata: ")[1];
          if (dataLine) {
            const content = JSON.parse(dataLine);
            partialText += content;

            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1].code = partialText;
              return updated;
            });
          }
        } else if (event.startsWith("event: end")) {
          setLastCode(partialText);
          fetch('https://holmio.net/app/api_get_credits.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId })
          })
            .then(res => res.json())
            .then(data => setCredits(data.credits));
        }
      });
    }

    setLoading(false);
  };

  const handleDownloadCode = (code) => {
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ESP32_code.ino';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpdateDiagram = async () => {
    if (!diagramInstruction.trim()) return;
    try {
      const res = await fetch('https://holmio.net/app/api_esp32.php?diagram=1', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, codigo: lastCode, modification: diagramInstruction })
      });
      const data = await res.json();
      if (data.error) {
        setMessages(prev => [...prev, { role: 'assistant', text: data.error }]);
      } else if (data.diagramUrl) {
        setDiagramUrl(data.diagramUrl);
        setCredits(data.tokensRestantes);
        setDiagramInstruction('');
      }
    } catch (error) {
      console.error('Error al actualizar diagrama:', error);
    }
  };

  const handleBuyCredits = async () => {
    setShowModal(true);
    try {
      const res = await fetch('https://holmio.net/app/api_get_credit_plans.php');
      const data = await res.json();
      setPlans(data);
    } catch (error) {
      console.error('Error cargando planes:', error);
    }
  };

  return (
    <div className="flex justify-center items-start min-h-[100vh] bg-gray-50 p-4">
      <div className="w-full max-w-6xl bg-white rounded-xl shadow-lg p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-4 border-b pb-2">
          <h2 className="text-2xl font-bold text-blue-700">Generador de código para ESP32 || </h2>
          <div className="flex items-center gap-4">
            {credits !== null && <span className="text-md text-gray-700">Créditos: {credits}</span>}
            <button
              className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
              onClick={handleBuyCredits}
            >
              Comprar Créditos
            </button>
          </div>
        </div>

        {credits === 0 && (
          <div className="bg-red-100 text-red-700 p-3 rounded mb-4 text-center font-semibold">
            No tienes créditos suficientes. Compra más para continuar.
          </div>
        )}

        {/* Chat */}
        <div ref={chatRef} className="flex-1 overflow-y-auto mb-8 p-8 border rounded bg-gray-50" style={{ maxHeight: '500px' }}>
          {messages.map((msg, i) => (
            <div key={i} className={`mb-3 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
              <div className={`inline-block px-3 py-2 rounded-lg ${msg.role === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>
                {msg.text}
              </div>
              {msg.code && (
                <div className="mt-2 bg-black text-white rounded">
                  <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
                    <SyntaxHighlighter
                      language="cpp"
                      style={vscDarkPlus}
                      customStyle={{ whiteSpace: 'pre', wordWrap: 'normal', minWidth: '800px' }}
                    >
                      {msg.code}
                    </SyntaxHighlighter>
                  </div>
                  <button onClick={() => handleDownloadCode(msg.code)} className="bg-blue-600 text-white px-3 py-1 m-2 rounded">
                    Descargar .ino
                  </button>
                </div>
              )}
            </div>
          ))}

          {diagramUrl && (
            <div className="mt-4 text-center">
              <h3 className="font-semibold mb-2">Diagrama generado:</h3>
              <div style={{ maxWidth: '500px', maxHeight: '500px', overflow: 'auto', border: '1px solid #ccc', borderRadius: '8px', padding: '4px', margin: '0 auto' }}>
                <img src={diagramUrl} alt="Diagrama ESP32" style={{ width: '100%', height: 'auto' }} />
              </div>
              <div className="mt-3">
                <input
                  type="text"
                  value={diagramInstruction}
                  onChange={(e) => setDiagramInstruction(e.target.value)}
                  placeholder="Ej: agrega un relé, cambia sensor"
                  className="border rounded px-3 py-2 w-3/4"
                  disabled={credits === 0}
                />
                <button
                  onClick={handleUpdateDiagram}
                  disabled={credits === 0}
                  className="ml-2 bg-indigo-600 text-white px-3 py-2 rounded hover:bg-indigo-700"
                >
                  Actualizar diagrama
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ej: Genera código o escribe 'Generar diagrama para este código'"
            className="flex-1 border px-3 py-2 rounded"
            disabled={credits === 0}
          />
          <button
            onClick={handleSend}
            disabled={loading || credits === 0}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? 'Generando...' : 'Enviar'}
          </button>
        </div>

        {/* Modal */}
        {showModal && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl">
              {!selectedPlan ? (
                <>
                  <h2 className="text-lg font-bold mb-2">Elige un paquete</h2>
                  <p className="text-gray-500 text-sm mb-4">1 crédito = 1 generación de código o diagrama</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {plans.map(plan => (
                      <div key={plan.id} className="border rounded-lg shadow-md p-4 flex flex-col justify-between hover:shadow-lg transition">
                        <h3 className="font-semibold text-lg mb-2 text-blue-600">{plan.name}</h3>
                        <p className="text-gray-700 text-md mb-1">{plan.credits} créditos</p>
                        <p className="text-green-600 font-bold text-xl mb-4">${plan.price} USD</p>
                        <button
                          onClick={() => setSelectedPlan(plan)}
                          className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700"
                        >
                          Comprar
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-center mt-6">
                    <button
                      className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                      onClick={() => setShowModal(false)}
                    >
                      Cerrar
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-lg font-bold mb-4">Confirmar Pago</h2>
                  <p className="text-xl font-semibold mb-4">${selectedPlan.price} USD</p>
                  <p>{selectedPlan.credits} créditos</p>
                  <PayPalButton
                    amount={selectedPlan.price}
                    description={`${selectedPlan.name} - ${selectedPlan.credits} créditos`}
                    onSuccess={async () => {
                      await fetch('https://holmio.net/app/api_add_credits.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ userId, credits: selectedPlan.credits })
                      });
                      alert(`Pago exitoso. Se agregaron ${selectedPlan.credits} créditos.`);
                      setShowModal(false);
                      setSelectedPlan(null);
                      const res = await fetch('https://holmio.net/app/api_get_credits.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ userId })
                      });
                      const data = await res.json();
                      setCredits(data.credits);
                    }}
                    onError={(error) => alert(`Error en el pago: ${error.message}`)}
                  />
                  <div className="flex justify-center mt-4">
                    <button
                      className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                      onClick={() => setSelectedPlan(null)}
                    >
                      Volver
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ESP32Generator;
